import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Trophy,
  Gift,
  RefreshCw,
  Crown,
  Star,
  Clock,
  Users,
  Sparkles,
  ChevronRight,
  Medal,
  Share2,
  Video,
} from "lucide-react";
import arthereumProvider from "../../../backend/ArthereumProvider";
import piNetworkAdsManager from "../../utils/PiNetworkAdsManager";
import { UserModelData } from "../../../types";
import { actions } from "../../redux";
import CommonPageTitle from "../../components/CommonPageTitle";
import LoadingIndicator from "../../components/LoadingIndicator";
import { useTranslationHook } from "../../hooks/useTranslationHook";

interface Winner {
  id: string;
  date: string;
  username: string;
  profileImage: string | null;
  points: number;
  prizeAmount: number;
  userId: string;
}

interface Contestant {
  id: string;
  userId: string;
  username: string;
  profileImage: string | null;
  points: number;
  joinedAt: string;
}

const RewardsPage: React.FC = () => {
  const dispatch = useDispatch();
  const userData: UserModelData = useSelector(
    (state: any) => state?.actualUser
  );
  const contestants: Contestant[] = useSelector(
    (state: any) => state?.dailyContestants || []
  );
  const winners: Winner[] = useSelector(
    (state: any) => state?.contestWinners || []
  );
  const isInContest: boolean = useSelector(
    (state: any) => state?.isInTodayContest || false
  );
  const getTranslationText = useTranslationHook();

  const [isLoadingContestants, setIsLoadingContestants] = useState(true);
  const [isLoadingWinners, setIsLoadingWinners] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [timeToMidnight, setTimeToMidnight] = useState("");
  const [isWatchingAdForPoints, setIsWatchingAdForPoints] = useState(false);
  const [adPointsWatchedToday, setAdPointsWatchedToday] = useState(0);

  // Calculate time until midnight
  useEffect(() => {
    const calculateTimeToMidnight = () => {
      const now = new Date();
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      const diff = midnight.getTime() - now.getTime();

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeToMidnight(
        `${hours.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
      );
    };

    calculateTimeToMidnight();
    const interval = setInterval(calculateTimeToMidnight, 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchContestants = useCallback(async () => {
    try {
      const data = await arthereumProvider.getDailyContestants();
      dispatch(actions.setDailyContestants(data.contestants));
    } catch (error) {
      console.error("Error fetching contestants:", error);
    } finally {
      setIsLoadingContestants(false);
    }
  }, [dispatch]);

  const handleShareRank = async () => {
    if (isSharing || !userData?.user?.id) return;
    setIsSharing(true);
    try {
      const shareText = `I'm competing in the Arthereum Daily Contest! Join me and win Pi. My code: ${userData.user.username}`;
      
      if (navigator.share) {
        await navigator.share({
          title: "Arthereum Wallet Contest",
          text: shareText,
          url: window.location.origin,
        });
      }
    } catch (error) {
      console.error("Error sharing rank:", error);
    } finally {
      setIsSharing(false);
    }
  };
  const fetchWinners = useCallback(async () => {
    try {
      const data = await arthereumProvider.getDailyContestWinners(10);
      console.log("Fetched winners:", data);
      dispatch(actions.setContestWinners(data.winners));
    } catch (error) {
      console.error("Error fetching winners:", error);
    } finally {
      setIsLoadingWinners(false);
    }
  }, [dispatch]);

  const checkContestParticipation = useCallback(async () => {
    if (!userData?.user?.id) return;
    try {
      const inContest = await arthereumProvider.isUserInTodayContest(
        userData.user.id
      );
      dispatch(actions.setIsInTodayContest(inContest));
    } catch (error) {
      console.error("Error checking participation:", error);
    }
  }, [userData?.user?.id, dispatch]);

  useEffect(() => {
    fetchContestants();
    fetchWinners();
    checkContestParticipation();
  }, [fetchContestants, fetchWinners, checkContestParticipation]);

  // Load today's ad points count from localStorage
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const saved = localStorage.getItem(`contest_ad_points_${userData?.user?.id}_${today}`);
    if (saved) setAdPointsWatchedToday(parseInt(saved, 10));
  }, [userData?.user?.id]);

  const handleJoinContest = async () => {
    if (!userData?.user?.id) return;
    setIsJoining(true);
    try {
      await arthereumProvider.joinDailyContest(
        userData.user.id,
        userData.user.username,
        userData.user.profileImage
      );
      dispatch(actions.setIsInTodayContest(true));
      await fetchContestants();
    } catch (error) {
      console.error("Error joining contest:", error);
      alert("Failed to join contest. Please try again.");
    } finally {
      setIsJoining(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        fetchContestants(),
        fetchWinners(),
        checkContestParticipation(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleWatchAdForPoints = async () => {
    if (isWatchingAdForPoints || adPointsWatchedToday >= 5 || !userData?.user?.id) return;
    setIsWatchingAdForPoints(true);
    try {
      await piNetworkAdsManager.handleRewardedAdFlowFront(userData.user.id, async () => {
        const result = await arthereumProvider.awardContestPoints(userData.user.id, 'ad_watch');
        const today = new Date().toISOString().split('T')[0];
        const newCount = adPointsWatchedToday + 1;
        setAdPointsWatchedToday(newCount);
        localStorage.setItem(`contest_ad_points_${userData.user.id}_${today}`, newCount.toString());
      });
    } catch (error) {
      console.error('Ad for points error:', error);
    } finally {
      setIsWatchingAdForPoints(false);
    }
  };

  const getRankIcon = (index: number) => {
    if (index === 0) return <Crown className="w-5 h-5 text-yellow-500" />;
    if (index === 1) return <Medal className="w-5 h-5 text-gray-400" />;
    if (index === 2) return <Medal className="w-5 h-5 text-amber-600" />;
    return (
      <span className="w-5 h-5 flex items-center justify-center text-gray-500 font-semibold text-sm">
        {index + 1}
      </span>
    );
  };

  const userRank = contestants.findIndex(
    (c) => c.userId === userData?.user?.id
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-amber-50 pb-24">
      {/* Header */}
      <div className="px-4 pt-4">
        <CommonPageTitle
          padding={0}
          title={getTranslationText("rewards_daily_rewards")}
        />
      </div>

      {/* Hero Card */}
      <div className="px-4 mt-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-700 p-6 text-white shadow-xl"
        >
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-yellow-300" />
              <span className="text-purple-200 text-sm font-medium">
                {getTranslationText("rewards_daily_contest")}
              </span>
            </div>

            <h2 className="text-2xl font-bold mb-2">
              {getTranslationText("rewards_win_prize")}
            </h2>
            <p className="text-purple-200 text-sm mb-4">
              {getTranslationText("rewards_contest_desc")}
            </p>

            {/* Countdown */}
            <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-3 backdrop-blur-sm">
              <Clock className="w-5 h-5 text-yellow-300" />
              <div>
                <div className="text-xs text-purple-200">
                  {getTranslationText("rewards_time_remaining")}
                </div>
                <div className="text-xl font-mono font-bold tracking-wider">
                  {timeToMidnight}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Join Button or Status */}
      <div className="px-4 mt-4">
        {!isInContest ? (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleJoinContest}
            disabled={isJoining}
            className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-2xl shadow-lg shadow-orange-200 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isJoining ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Gift className="w-5 h-5" />
            )}
            {isJoining
              ? getTranslationText("rewards_joining")
              : getTranslationText("rewards_join_contest")}
          </motion.button>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Star className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="text-green-800 font-semibold">
                  {getTranslationText("rewards_youre_in")}
                </div>
                <div className="text-green-600 text-sm">
                  {userRank >= 0
                    ? getTranslationText("rewards_rank", { rank: userRank + 1 })
                    : getTranslationText("rewards_competing_today")}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-right">
                <div className="text-2xl font-bold text-green-700">
                  {contestants[userRank]?.points || 0}
                </div>
                <div className="text-green-600 text-xs">
                  {getTranslationText("rewards_points")}
                </div>
              </div>
              <button
                onClick={handleShareRank}
                disabled={isSharing}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-[10px] font-bold rounded-lg transition-colors shadow-sm"
              >
                <Share2 className="w-3 h-3" />
                SHARE RANK
              </button>
            </div>
          </div>
        )}
      </div>

      {/* How to Earn Points Section */}
      <div className="px-4 mt-6">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-gray-800">
            {getTranslationText("rewards_how_to_earn")}
          </h3>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Claim Grant */}
          <div className="flex items-center justify-between p-4 border-b border-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center">
                <Gift className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-medium text-gray-800">
                  {getTranslationText("rewards_claim_grant")}
                </div>
                <div className="text-sm text-gray-500">
                  {getTranslationText("rewards_claim_grant_desc")}
                </div>
              </div>
            </div>
            <div className="bg-purple-100 text-purple-700 font-bold px-3 py-1 rounded-full text-sm">
              {getTranslationText("rewards_pts", { pts: "3" })}
            </div>
          </div>

          {/* Watch Ads */}
          <div className="flex items-center justify-between p-4 border-b border-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <div className="font-medium text-gray-800">
                  {getTranslationText("rewards_watch_ads")}
                </div>
                <div className="text-sm text-gray-500">
                  {getTranslationText("rewards_watch_ads_desc")}
                </div>
              </div>
            </div>
            <div className="bg-green-100 text-green-700 font-bold px-3 py-1 rounded-full text-sm">
              {getTranslationText("rewards_pt_each", { pts: "1" })}
            </div>
          </div>

          {/* Daily Login */}
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                  />
                </svg>
              </div>
              <div>
                <div className="font-medium text-gray-800">
                  {getTranslationText("rewards_daily_login")}
                </div>
                <div className="text-sm text-gray-500">
                  {getTranslationText("rewards_daily_login_desc")}
                </div>
              </div>
            </div>
            <div className="bg-amber-100 text-amber-700 font-bold px-3 py-1 rounded-full text-sm">
              {getTranslationText("rewards_pts", { pts: "2" })}
            </div>
          </div>

          {/* Share Contest */}
          <div className="flex items-center justify-between p-4 border-t border-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-xl flex items-center justify-center">
                <Share2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-medium text-gray-800">
                  {getTranslationText("rewards_share_contest")}
                </div>
                <div className="text-sm text-gray-500">
                  {getTranslationText("rewards_share_contest_desc")}
                </div>
              </div>
            </div>
            <div className="bg-purple-100 text-purple-700 font-bold px-3 py-1 rounded-full text-sm">
              {getTranslationText("rewards_pts", { pts: "2" })}
            </div>
          </div>

          {/* Share Grant */}
          <div className="flex items-center justify-between p-4 border-t border-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-rose-500 rounded-xl flex items-center justify-center">
                <Share2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-medium text-gray-800">
                  {getTranslationText("rewards_share_grant")}
                </div>
                <div className="text-sm text-gray-500">
                  {getTranslationText("rewards_share_grant_desc")}
                </div>
              </div>
            </div>
            <div className="bg-rose-100 text-rose-700 font-bold px-3 py-1 rounded-full text-sm">
              {getTranslationText("rewards_pts", { pts: "1" })}
            </div>
          </div>

          {/* Watch Ad for Points */}
          <div className="flex items-center justify-between p-4 border-t border-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                <Video className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-medium text-gray-800">Watch Ads</div>
                <div className="text-sm text-gray-500">Watch up to 5 ads for bonus points</div>
              </div>
            </div>
            <button
              onClick={handleWatchAdForPoints}
              disabled={isWatchingAdForPoints || adPointsWatchedToday >= 5}
              className={`px-3 py-1.5 rounded-full text-sm font-bold transition-all ${
                adPointsWatchedToday >= 5
                  ? 'bg-gray-100 text-gray-400'
                  : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-md active:scale-95'
              }`}
            >
              {isWatchingAdForPoints ? '...' : adPointsWatchedToday >= 5 ? `5/5 ✓` : `+3 pts (${adPointsWatchedToday}/5)`}
            </button>
          </div>
        </div>
      </div>

      {/* Today's Leaderboard */}
      <div className="px-4 mt-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-gray-800">
              {getTranslationText("rewards_todays_leaderboard")}
            </h3>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-1 text-purple-600 text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-purple-50 transition-colors"
          >
            <RefreshCw
              className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
            {getTranslationText("rewards_refresh")}
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {isLoadingContestants ? (
            <div className="flex items-center justify-center py-12">
              <LoadingIndicator />
            </div>
          ) : contestants.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{getTranslationText("rewards_no_contestants")}</p>
              <p className="text-sm text-gray-400">
                {getTranslationText("rewards_be_first")}
              </p>
            </div>
          ) : (
            <AnimatePresence>
              {contestants.slice(0, 10).map((contestant, index) => (
                <motion.div
                  key={contestant.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`flex items-center justify-between p-4 border-b border-gray-50 last:border-b-0 ${
                    contestant.userId === userData?.user?.id
                      ? "bg-purple-50"
                      : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 flex justify-center">
                      {getRankIcon(index)}
                    </div>
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full flex items-center justify-center text-white font-semibold">
                      {contestant.username?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <div className="font-medium text-gray-800">
                        {contestant.username}
                        {contestant.userId === userData?.user?.id && (
                          <span className="ml-2 text-xs text-purple-600">
                            {getTranslationText("rewards_you")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="font-bold text-gray-800">
                      {contestant.points}
                    </span>
                    <Star className="w-4 h-4 text-amber-500" />
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>
      </div>

      {/* Previous Winners */}
      <div className="px-4 mt-6">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-5 h-5 text-amber-500" />
          <h3 className="font-semibold text-gray-800">
            {getTranslationText("rewards_previous_winners")}
          </h3>
        </div>

        {isLoadingWinners ? (
          <div className="flex items-center justify-center py-8">
            <LoadingIndicator />
          </div>
        ) : winners.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center text-gray-500">
            <Trophy className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>{getTranslationText("rewards_no_winners")}</p>
            <p className="text-sm text-gray-400">
              {getTranslationText("rewards_winners_appear_here")}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {winners.map((winner, index) => (
              <motion.div
                key={winner.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">
                      {winner.username}
                    </div>
                    <div className="text-sm text-gray-500">
                      {new Date(winner.date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-amber-600">
                    {winner.prizeAmount.toFixed(4)} π
                  </div>
                  <div className="text-xs text-gray-500">
                    {winner.points} pts
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom spacing for nav */}
      <div className="h-20" />
    </div>
  );
};

export default RewardsPage;
